// Pure seam between authored score parts and generated performance tracks.
//
// A caller supplies the already-expanded performance itinerary. This module
// deliberately does not expand repeats itself: the same occurrence list can
// therefore drive every authored and generated layer, including voltas,
// codas and repeated form measures.

import { frac, fractionToNumber, measureQuarters, mulF } from './duration';
import { midiOf } from './pitch';
import { voiceTiming } from './timing';
import { resolvePartMeasurePlayback } from './playbackRules';
import type { NoteEvent, PartRole, Score } from './types';

const EPSILON = 1e-9;

/** One written measure at one concrete position in performance order. */
export interface PerformanceMeasureOccurrence {
  formMeasureIndex: number;
  /** Absolute, zero-based onset in the playback beat domain. */
  startBeat: number;
  /** Sounding span of this occurrence in that same beat domain. */
  beats: number;
  /** The itinerary pass this occurrence belongs to, when the caller performs one. */
  passKind?: 'head' | 'solo' | 'head-out' | 'written';
}

/** Whether authored content owns a role for one performance occurrence. */
export interface PartPlaybackOwnership extends PerformanceMeasureOccurrence {
  occurrenceIndex: number;
  owned: boolean;
  /** Matching parts which contain authored content in this written measure. */
  ownerPartIds: string[];
}

/** A sounding note extracted from an authored score part. */
export interface AuthoredPartPlaybackNote {
  midi: number;
  /** Authored comp timbre, independent of the generated band's instrument. */
  instrument?: 'piano' | 'guitar';
  /** Absolute, zero-based onset in the playback beat domain. */
  beat: number;
  /** Full written duration, including every joined tie continuation. */
  durationBeats: number;
  /** Linear attack gain relative to an unmarked note (1). */
  velocity?: number;
  /** Sound this proportion of the full written duration without adding a
   * generated-bass release tail. Omitted preserves the ordinary bass envelope. */
  articulationGate?: number;
  partId: string;
  eventId: string;
  occurrenceIndex: number;
  formMeasureIndex: number;
}

export interface AuthoredPartPlayback {
  role: PartRole;
  notes: AuthoredPartPlaybackNote[];
  ownership: PartPlaybackOwnership[];
}

/** Common timing shape used by the generated bass and melody tracks. */
export interface PlaybackBeatNote {
  midi: number;
  beat: number;
  durationBeats: number;
}

function isEditorPadding(event: NoteEvent): boolean {
  return event.kind === 'rest' && /^r[:-]/.test(event.id);
}

function timeSignatureAt(score: Score, measureIndex: number): [number, number] {
  let meter: [number, number] = [4, 4];
  for (let index = 0; index <= measureIndex && index < score.form.length; index += 1) {
    const next = score.form[index].timeSig;
    if (next) meter = next;
  }
  return meter;
}

/** Written capacity in quarter notes, including a fractional pickup. */
function measureCapacityQuarters(score: Score, measureIndex: number): number {
  const meter = timeSignatureAt(score, measureIndex);
  const pickup = score.form[measureIndex]?.pickupBeats;
  const capacity = pickup == null ? measureQuarters(meter) : mulF(frac(pickup), frac(4, meter[1]));
  return fractionToNumber(capacity);
}

function soundingHeads(event: NoteEvent, role: PartRole) {
  if (role !== 'bass' || event.notes.length <= 1) return event.notes;
  return [
    event.notes.reduce((lowest, head) =>
      midiOf(head.pitch) < midiOf(lowest.pitch) ? head : lowest,
    ),
  ];
}

/** Articulations belong to the attack of a tied sound, never its continuations.
 * Keep gate separate from written duration so ties and form timing stay exact. */
function bassArticulationPlayback(
  event: NoteEvent,
): Pick<AuthoredPartPlaybackNote, 'velocity' | 'articulationGate'> {
  const marks = event.articulations ?? [];
  const velocity = marks.includes('marcato') ? 1.35 : marks.includes('accent') ? 1.2 : undefined;
  const articulationGate = marks.includes('staccato')
    ? 0.5
    : marks.includes('tenuto')
      ? 1
      : marks.includes('marcato')
        ? 0.75
        : undefined;
  return {
    ...(velocity !== undefined ? { velocity } : {}),
    ...(articulationGate !== undefined ? { articulationGate } : {}),
  };
}

interface OpenTie {
  note: AuthoredPartPlaybackNote;
  endBeat: number;
}

/**
 * Project parts carrying `role` onto an explicit, already-expanded playback
 * itinerary. Pitches in Score are concert truth, so no Part transposition is
 * applied here.
 *
 * Ownership is deliberately bar-wide. Any authored event (including an
 * explicit rest) suppresses generation for the whole role/occurrence. Empty
 * measures, improvisation slashes and editor-created `r:` / `r-` padding rests
 * do not. Grace notes
 * claim ownership but do not become timed playback notes.
 */
export function buildAuthoredPartPlayback(
  score: Score,
  role: PartRole,
  occurrences: readonly PerformanceMeasureOccurrence[],
): AuthoredPartPlayback {
  const matchingParts = score.parts.filter((part) => part.role === role);
  const notes: AuthoredPartPlaybackNote[] = [];
  const ownership: PartPlaybackOwnership[] = [];
  const openTies = new Map<string, OpenTie>();
  const visits = new Map<number, number>();

  occurrences.forEach((occurrence, occurrenceIndex) => {
    const visit = (visits.get(occurrence.formMeasureIndex) ?? 0) + 1;
    visits.set(occurrence.formMeasureIndex, visit);
    const ownerPartIds: string[] = [];
    const capacityQuarters = measureCapacityQuarters(score, occurrence.formMeasureIndex);
    const beatsPerQuarter = capacityQuarters > EPSILON ? occurrence.beats / capacityQuarters : 0;

    matchingParts.forEach((part) => {
      // A part written for the heads yields the solo choruses to the generated band.
      if (part.playOn === 'heads' && occurrence.passKind === 'solo') return;
      const measure = part.measures[occurrence.formMeasureIndex];
      if (!measure) return;

      const timedVoices = measure.voices.map((voice) => voiceTiming(voice));
      const ownsMeasure = timedVoices.some((events) =>
        events.some(({ event }) => !isEditorPadding(event) && !event.slash),
      );
      if (!ownsMeasure) return;
      ownerPartIds.push(part.id);

      timedVoices.forEach((events, voiceIndex) => {
        events.forEach(({ event, start, duration }) => {
          const { silent, octaveShift } = resolvePartMeasurePlayback(
            measure,
            visit,
            event.staff ?? measure.voices[voiceIndex].staff ?? 0,
            start,
          );
          if (
            silent ||
            isEditorPadding(event) ||
            event.grace ||
            event.slash ||
            event.kind === 'rest' ||
            event.notes.length === 0
          ) {
            return;
          }

          const beat = occurrence.startBeat + fractionToNumber(start) * beatsPerQuarter;
          const durationBeats = fractionToNumber(duration) * beatsPerQuarter;
          for (const head of soundingHeads(event, role)) {
            const midi = midiOf(head.pitch) + 12 * octaveShift;
            const tieKey = `${part.id}\u0000${voiceIndex}\u0000${midi}`;
            const open = openTies.get(tieKey);
            const stopsTie = head.tie === 'stop' || head.tie === 'both';
            const startsTie = head.tie === 'start' || head.tie === 'both';

            if (open && stopsTie && Math.abs(open.endBeat - beat) <= EPSILON) {
              open.note.durationBeats += durationBeats;
              open.endBeat = beat + durationBeats;
              if (!startsTie) openTies.delete(tieKey);
              continue;
            }

            // A malformed or performance-order-broken chain must not consume
            // a later equal pitch merely because its written tie flag survives.
            openTies.delete(tieKey);
            const note: AuthoredPartPlaybackNote = {
              midi,
              beat,
              durationBeats,
              partId: part.id,
              eventId: event.id,
              occurrenceIndex,
              formMeasureIndex: occurrence.formMeasureIndex,
              ...(role === 'comp' && (part.instrument === 'piano' || part.instrument === 'guitar')
                ? { instrument: part.instrument }
                : {}),
              ...(role === 'bass' ? bassArticulationPlayback(event) : {}),
            };
            notes.push(note);
            if (startsTie) openTies.set(tieKey, { note, endBeat: beat + durationBeats });
          }
        });
      });
    });

    ownership.push({
      ...occurrence,
      occurrenceIndex,
      owned: ownerPartIds.length > 0,
      ownerPartIds,
    });
  });

  notes.sort(
    (a, b) =>
      a.beat - b.beat ||
      a.midi - b.midi ||
      a.occurrenceIndex - b.occurrenceIndex ||
      a.partId.localeCompare(b.partId),
  );
  return { role, notes, ownership };
}

export interface OwnedSpan {
  start: number;
  end: number;
}

function ownedSpans(ownership: readonly PartPlaybackOwnership[]): OwnedSpan[] {
  const spans = ownership
    .filter(({ owned, beats }) => owned && beats > EPSILON)
    .map(({ startBeat, beats }) => ({ start: startBeat, end: startBeat + beats }))
    .sort((a, b) => a.start - b.start || a.end - b.end);
  const merged: OwnedSpan[] = [];
  for (const span of spans) {
    const previous = merged[merged.length - 1];
    if (previous && span.start <= previous.end + EPSILON) {
      previous.end = Math.max(previous.end, span.end);
    } else {
      merged.push({ ...span });
    }
  }
  return merged;
}

/**
 * Remove generated attacks from authored occurrences. A generated note which
 * starts earlier but rings into an authored occurrence is shortened exactly
 * to that boundary, preventing doubled bass at the hand-off.
 */
export function filterGeneratedPartNotes<T extends PlaybackBeatNote>(
  generated: readonly T[],
  ownership: readonly PartPlaybackOwnership[],
): T[] {
  return restGeneratedIn(generated, ownedSpans(ownership));
}

/**
 * The choruses a bass playing the written melody takes for itself (Oct 2026,
 * user on Tricotism and Ladies in Mercedes: "the bass takes the melody — I
 * would still want it to revert to auto bassline on the choruses"): every
 * pass of the played form in which the melody sounds. A solo chorus under
 * head, solos, out carries no melody, so the generated line walks there and
 * stops again for the head out; a form played as written is the head every
 * time round. With no pass grid the whole form is one pass.
 */
export function melodyPassSpans(
  passStarts: readonly number[] | undefined,
  formBeats: number,
  melody: readonly { beat: number }[],
): OwnedSpan[] {
  const starts = passStarts?.length ? passStarts : [0];
  return starts.flatMap((start, index) => {
    const end = starts[index + 1] ?? formBeats;
    return end > start + EPSILON &&
      melody.some(({ beat }) => beat >= start - EPSILON && beat < end - EPSILON)
      ? [{ start, end }]
      : [];
  });
}

/**
 * Remove generated attacks inside the spans, shortening a note that starts
 * before one and rings into it exactly to its boundary.
 */
export function restGeneratedIn<T extends PlaybackBeatNote>(
  generated: readonly T[],
  spans: readonly OwnedSpan[],
): T[] {
  if (spans.length === 0) return [...generated];

  const filtered: T[] = [];
  for (const note of generated) {
    const start = note.beat;
    const end = start + Math.max(0, note.durationBeats);
    if (spans.some((span) => start >= span.start - EPSILON && start < span.end - EPSILON)) {
      continue;
    }
    const boundary = spans.find(
      (span) => span.start > start + EPSILON && span.start < end - EPSILON,
    );
    if (!boundary) {
      filtered.push(note);
      continue;
    }
    const durationBeats = boundary.start - start;
    if (durationBeats > EPSILON) filtered.push({ ...note, durationBeats });
  }
  return filtered;
}

/** Filter generation, add authored notes, and return one chronological track. */
export function mergeGeneratedWithAuthored<T extends PlaybackBeatNote>(
  generated: readonly T[],
  authored: AuthoredPartPlayback,
): Array<T | AuthoredPartPlaybackNote> {
  return [...filterGeneratedPartNotes(generated, authored.ownership), ...authored.notes].sort(
    (a, b) => a.beat - b.beat || a.midi - b.midi,
  );
}
